<script lang="ts">
  import { spaNavigate } from "@kontextwerk/control-web-frame/navigation"
  import { adminSession } from "../../../../_shared/_logic/admin-session"
  import { userInitials, userDisplayName } from "@kontextwerk/control-web-frame/logic/userDisplay"
  import HomeOverview from "./_modules/home-overview"
  import {
    hasActivePlanningEmployee,
    resolveHomeDashboardAccess,
  } from "../../_shared/_logic/home-dashboard"
  import { resolveAllowedEmployeeIds } from "../../../../../_shared/_logic/permissions-model"
  import type { OrgaViewFrameContext } from "../../../../context"
  import type { PageContext } from "../../navigation"

  const { viewFrameContext, pageContext } = $props<{
    viewFrameContext: OrgaViewFrameContext
    pageContext: PageContext
  }>()

  const canViewNotifications = $derived(
    pageContext.permissions.allows("core.notifications", "view", { scope: "user" }) ||
      pageContext.permissions.allows("core.notifications", "view", { scope: "org" }) ||
      pageContext.permissions.allows("core.notifications", "manage", { scope: "user" }) ||
      pageContext.permissions.allows("core.notifications", "manage", { scope: "org" }),
  )
  const dashboardAccess = $derived(resolveHomeDashboardAccess(pageContext.permissions))
  const allowedManageEmployeeIds = $derived(
    resolveAllowedEmployeeIds(
      pageContext.permissions,
      "termin.scheduling",
      viewFrameContext.currentUserId,
      "manage",
    ),
  )
  const canUseOperationalPlanning = $derived(
    dashboardAccess.canUseOperationalPlanning &&
      hasActivePlanningEmployee(pageContext.organization?.users, allowedManageEmployeeIds),
  )
  const canManageSetup = $derived(
    viewFrameContext.permissions.isSuper ||
      viewFrameContext.permissions.allows("core.org_settings", "manage"),
  )

  const firstName = $derived.by(() => {
    const user = $adminSession?.user ?? null

    return userInitials(user).firstName ?? userDisplayName(user)
  })

  const welcomeTitle = $derived.by(() => (firstName ? `Willkommen, ${firstName}!` : "Willkommen!"))

  const notificationsPagePath = $derived.by(() => {
    if (!canViewNotifications) return null

    const orgSlug = viewFrameContext.organization?.slug ?? null

    if (!orgSlug) return null

    return `/organizations/${encodeURIComponent(orgSlug)}/notifications`
  })

  const openNotifications = () => {
    if (notificationsPagePath) {
      spaNavigate(notificationsPagePath)
    }
  }
</script>

<div class="home-page">
  <h1>{welcomeTitle}</h1>
  {#if pageContext.organization && viewFrameContext.currentUserId}
    {#key `${pageContext.organization.id}:${viewFrameContext.currentUserId}`}
      <HomeOverview
        organization={pageContext.organization}
        currentUserId={viewFrameContext.currentUserId}
        inboxPath={notificationsPagePath}
        onOpenInbox={openNotifications}
        {canManageSetup}
        {canUseOperationalPlanning}
        showOpenTasks={canViewNotifications}
      />
    {/key}
  {/if}
</div>

<style>
  .home-page {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-width: 0;
    width: 100%;
    color: var(--text-invers-100);
  }

  h1 {
    margin: 0;
    color: var(--text-invers-100);
    font-size: 1.1rem;
    line-height: 1.4;
    font-weight: 600;
  }
</style>
